/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import {NgClass} from '@angular/common';
import {NlrPermissionsDisabledPipe} from '../../../pipe/nlr-permissions-disabled-pipe';
import {NlrFormFieldHasErrorPipe} from '../../../pipe/nlr-form-field-has-error.pipe';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {ModalComponent} from '../modal-service.service';
import {PlanModelService} from '../../../service/plan-model.service';
import {LookupService} from '../../../service/lookup.service';
import {NlrTypeheadDirective} from '../../../directive/nlr-typehead.directive';
import {debounceTime, distinctUntilChanged, map, mergeMap, Observable, of, OperatorFunction} from 'rxjs';
import {NlrPatternDirective} from '../../../directive/nlr-pattern.directive';
import {PATTERNS} from '../../../constants';
import {BurdensAffectedTenureComponent} from '../../../../interests/burdens-affected-tenure/burdens-affected-tenure.component';
import {equals, isDefined} from '../../../service/data-util';
import {NlrPatternExcludeParserDirective} from '../../../directive/nlr-pattern-exclude-parser.directive';
import {ConfirmationModalService} from '../confirmation-modal/confirmation-modal.service';
import {SpatialUtilService} from '../../../../design/services/spatial-util.service';
import {NgSelectModule} from '@ng-select/ng-select';
import {NlrHasPermissionsPipe} from '../../../pipe/nlr-has-permissions.pipe';
import {NlrFocusDirective} from '../../../directive/nlr-focus.directive';

@Component({
    selector: 'new-interests-notification-modal',
    imports: [
        NlrPermissionsDisabledPipe,
        NlrFormFieldHasErrorPipe,
        NgClass,
        FormsModule,
        NlrTypeheadDirective,
        NlrPatternDirective,
        BurdensAffectedTenureComponent,
        NlrPatternExcludeParserDirective,
        NgSelectModule,
        NlrHasPermissionsPipe,
        NlrFocusDirective
    ],
    templateUrl: './new-interest-notification-modal.component.html',
    styleUrls: ['../modal.scss', './new-interest-notification-modal.component.scss'],
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class NewInterestsNotificationModalComponent implements ModalComponent, OnInit {
    @Input()
    interest: any;

    modalClosed: EventEmitter<string | any> = new EventEmitter();
    title = 'New Interest / Notification';
    subjectPattern = PATTERNS.ALPHANUMERIC_AND_LENGTH_PATTERN;
    originDocumentNumberPattern = PATTERNS.DOCUMENT_PATTERN;
    lotsPattern = PATTERNS.LOT_PATTERN;
    surveyPattern = PATTERNS.SURVEY_PATTERN;

    interestsUiConfig: any;
    planPurposeLookup: any;
    parentTenureType: any;
    interestNotificationType: any;
    interestNotificationTypeName = '';
    localGovernmentAuthorities: any;

    // New form variables
    id = undefined; // Entity ID
    beneficiaryOptions = [];
    beneficiarySelected = true;

    createdByOptions = [];
    createdByOptionSelected = undefined;
    namedBeneficiaryOptions = [];
    namedBeneficiarySelected = undefined;

    burdenedLots = [];
    beneficiaryLots = [];
    beneficiaryLotsOtherPlan = [];
    interestNotificationTypes = [];
    localGovernmentAuthoritySelected = undefined;

    benefitTypes = [];
    selectedNamedBeneficiaries = [];

    burdenedAffectedTenures = [];

    //-------- variables ------------------------------
    comments = undefined;
    sameOwner = undefined;

    originDocumentNumber = undefined;
    subject = undefined;
    statutoryReference = undefined;
    purpose = undefined;
    selectedBeneficiary = undefined;
    beneficiaryDescription = undefined;
    additionalMandatoryBeneficiary = undefined;

    @ViewChild('newInterestNotificationModalForm')
    newInterestNotificationModalForm: NgForm;

    newInterestSameOwnerValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    constructor(
        private planModelService: PlanModelService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService,
        private confirmationModalService: ConfirmationModalService
    ) {}

    ngOnInit(): void {
        this.planPurposeLookup = this.lookupService.getLookups('planPurpose').find(v => v.id === this.planModelService.getModel().planPurpose.id);
        this.parentTenureType = this.planPurposeLookup.parentTenureAssociationType;
        this.interestNotificationTypes = this.lookupService.getLookups('interest');
        this.benefitTypes = this.lookupService.getLookups('interestBenefit');
        this.lookupService
            .retrieveLookup('/locationname/findAllLocalGovernmentAuthorities')
            .subscribe(data => (this.localGovernmentAuthorities = data));
        this.interestsUiConfig = this.lookupService
            .getLookups('interestsUiConfig')
            .find(
                v =>
                    v.parentTenureAssociationType === this.parentTenureType &&
                    v.pdfCreationMethod === this.planModelService.getModel().pdfCreationMethod
            );

        if (this.interest) {
            this.convertFromDomainToUI(this.interest);
            this.initializeForm();
        } else {
            this.burdenedLots.push({lot: '', apportionment: ''});
            this.beneficiaryLots.push({lot: '', apportionment: ''});
            this.beneficiaryLotsOtherPlan = [{planNumber: '', beneficiaryLots: [{lot: '', apportionment: ''}]}];
        }
    }

    closeDialog() {
        this.modalClosed.emit('Closed');
    }

    readonly() {
        return this.planModelService.readonly;
    }

    interestNotificationTypeModelChange = () => {
        this.interestNotificationType = null;
    };

    interestNotificationTypeChange = (form: NgForm) => {
        if (!this.interestNotificationType) {
            form.controls['newInterestNotification'].setErrors({
                invalid: true
            });
            this.interestNotificationTypeName = '';
        } else {
            form.controls['newInterestNotification'].setErrors(null);
        }
    };

    interestNotificationTypeSelected = item => {
        this.interestNotificationType = this.interestNotificationTypes.filter(v => v.name.toLowerCase() === item.toLowerCase())[0];
        this.interestNotificationTypeName = this.interestNotificationType.name;
        this.purpose = this.interestNotificationType.purpose;
        this.statutoryReference = this.interestNotificationType.statutoryReference;
        this.createdByOptionSelected = undefined;
        this.namedBeneficiarySelected = undefined;
        this.selectedBeneficiary = undefined;
        this.beneficiarySelected = true;
        this.additionalMandatoryBeneficiary = undefined;

        this.initializeForm();
    };

    newInterestNotificationTypeahead: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map(term => this.filterInterestNotification(term).map(t => t.name))
        );

    filterInterestNotification = viewValue => {
        return this.interestNotificationTypes.filter(
            v =>
                v.active &&
                v.validPlanPurposes.includes(this.planModelService.getModel().planPurpose.id) &&
                v.keywords.toLowerCase().includes(viewValue.toLowerCase())
        );
    };

    lgaTypeahead: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map(term => this.filterLga(term).map(t => t.localGovernmentAuthority))
        );

    filterLga = viewValue => {
        return this.localGovernmentAuthorities.filter(lga => {
            return lga.localGovernmentAuthority.toLowerCase().indexOf(viewValue.toLowerCase()) !== -1;
        });
    };

    initializeForm = () => {
        this.createdByOptions.length = 0;
        this.beneficiaryOptions.length = 0;
        this.namedBeneficiaryOptions.length = 0;
        this.selectedNamedBeneficiaries.length = 0;

        if (this.interestNotificationType && this.interestNotificationType.origin) {
            const origins = this.interestNotificationType.origin.trim().split(',');

            if (origins.includes('PLAN')) {
                this.createdByOptions.push({value: 'PLAN', displayValue: 'This plan'});
                if (!this.createdByOptionSelected) {
                    this.createdByOptionSelected = 'PLAN';
                }
            }
            if (origins.includes('DOCUMENT')) {
                this.createdByOptions.push({value: 'DOCUMENT', displayValue: 'Document'});
                if (!this.createdByOptionSelected) {
                    this.createdByOptionSelected = 'DOCUMENT';
                }
            }
            if (origins.includes('DOCUMENT_AND_PLAN')) {
                this.createdByOptions.push({value: 'DOCUMENT_AND_PLAN', displayValue: 'This plan and document'});
                if (!this.createdByOptionSelected) {
                    this.createdByOptionSelected = 'DOCUMENT_AND_PLAN';
                }
            }
        }

        if (
            (this.interestNotificationType.benefitLotsThis && this.interestNotificationType.benefitLotsThis.includes(this.parentTenureType)) ||
            (this.interestNotificationType.benefitLotsOther && this.interestNotificationType.benefitLotsOther.includes(this.parentTenureType))
        ) {
            this.beneficiaryOptions.push({value: true, displayValue: 'Benefit lots', toolTip: 'Benefit lots'});
        }

        if (this.interestNotificationType.benefitCategory !== 'none') {
            this.beneficiaryOptions.push({value: false, displayValue: 'Named beneficiaries', toolTip: 'Named beneficiaries'});
            if (this.beneficiaryOptions.length === 1) {
                // Only named beneficiaries available
                this.beneficiarySelected = false;
            }

            const lga = this.interestNotificationType.benefitCategory.indexOf('lga') !== -1;
            const benefittype = this.interestNotificationType.benefitCategory.indexOf('benefittype') !== -1;
            const freetext = this.interestNotificationType.benefitCategory.indexOf('freetext') !== -1;
            const seedoc = this.interestNotificationType.benefitCategory.indexOf('seedoc') !== -1;

            if (lga) {
                this.namedBeneficiaryOptions.push({value: 'lga', displayValue: 'Local government', dataModelValue: 'LGA'});
            }

            if (benefittype) {
                this.namedBeneficiaryOptions.push({
                    value: 'benefittype',
                    displayValue: 'Select beneficiary',
                    dataModelValue: 'SELECTED_BENEFICIARY'
                });

                // Filter out selected benefit types
                this.selectedNamedBeneficiaries.splice(0, this.selectedNamedBeneficiaries.length, ...this.getBenefitTypesForInterest());

                // default the beneficiary selection
                if (!this.selectedBeneficiary) {
                    this.selectedNamedBeneficiaries.forEach(beneficiary => {
                        if (beneficiary.isDefaultValue) {
                            this.selectedBeneficiary = beneficiary;
                        }
                    });
                }
            }

            if (freetext) {
                this.namedBeneficiaryOptions.push({
                    value: 'freetext',
                    displayValue: 'Free text beneficiary',
                    dataModelValue: 'FREE_TEXT_BENEFICIARY'
                });
            }

            if (seedoc) {
                this.namedBeneficiaryOptions.push({value: 'seedoc', displayValue: 'See document', dataModelValue: 'SEE_DOCUMENT'});
            }

            if (!this.namedBeneficiarySelected) {
                if (lga) {
                    this.namedBeneficiarySelected = 'lga';
                } else if (benefittype) {
                    this.namedBeneficiarySelected = 'benefittype';
                } else if (freetext) {
                    this.namedBeneficiarySelected = 'freetext';
                } else if (seedoc) {
                    this.namedBeneficiarySelected = 'seedoc';
                } else {
                    this.namedBeneficiarySelected = undefined;
                    this.beneficiarySelected = true;
                }
            }

            this.additionalMandatoryBeneficiary = this.interestNotificationType.additionalMandatoryBeneficiary;
        }
    };

    getBenefitTypesForInterest = () => {
        if (this.interestNotificationType && this.interestNotificationType.benefitTypes) {
            const codes = this.interestNotificationType.benefitTypes.split(',');
            let benefits = this.benefitTypes.filter(b => codes.includes(b.type)).map(b => structuredClone(b));
            if (benefits.length === 0) {
                // check for a matching code instead
                benefits = this.benefitTypes.filter(b => codes.includes(b.code)).map(b => structuredClone(b));
            }
            return benefits;
        }
        return [];
    };

    isShowCreatedBy = () => {
        return this.interestNotificationType;
    };

    isShowSubject = () => {
        return this.interestNotificationType;
    };

    isShowOriginDocumentNumber = () => {
        return this.interestNotificationType && (this.createdByOptionSelected === 'DOCUMENT' || this.createdByOptionSelected === 'DOCUMENT_AND_PLAN');
    };

    showBurdensAffectedTenure = () => {
        return (
            this.interestNotificationType &&
            this.interestNotificationType.burdensAffectedTenure &&
            this.interestNotificationType.burdensAffectedTenure.includes(this.parentTenureType)
        );
    };

    showBurdensNewLotsOnThisPlan = () => {
        return this.planPurposeLookup.maxNumberLotsCreated > 0;
    };

    lotChanged = (form, name, lotCollection, element, benefitingDominantLot) => {
        console.debug(this.title + ' controller lot Changed');

        const startLot = this.getStartLot(benefitingDominantLot.lot);
        const endLot = this.getEndLot(benefitingDominantLot.lot);

        if (
            isDefined(endLot) &&
            isDefined(startLot) &&
            ((name == 'burdenedLots' && this.isSingleBurdenLot() && startLot != endLot) || startLot > endLot)
        ) {
            form.controls[name + 'lot' + element].dirty = true;
            form.controls[name + 'lot' + element].setErrors({
                invalidLotOrRange: true
            });
        } else {
            const duplicates = lotCollection.filter(bsLot => {
                return bsLot.lot !== '' && benefitingDominantLot.lot == bsLot.lot;
            });

            if (duplicates.length > 1) {
                form.controls[name + 'lot' + element].dirty = true;
                form.controls[name + 'lot' + element].setErrors({duplicate: true});
            }
        }
    };

    getStartLot = lots => {
        if (isDefined(lots)) {
            if (!isNaN(lots.trim())) {
                return Number(lots.trim());
            } else if (isNaN(lots) && lots.indexOf('-') > 0) {
                return Number(lots.split('-')[0].trim());
            }
        }

        return undefined;
    };

    getEndLot = lots => {
        if (isDefined(lots)) {
            if (isNaN(lots) && lots.indexOf('-') > 0) {
                return Number(lots.split('-')[1].trim());
            }
        }

        return undefined;
    };

    isSingleBurdenLot = () => {
        if (this.interestNotificationType && this.interestNotificationType.singleBurdenLotEntry) {
            return this.interestNotificationType.singleBurdenLotEntry === 'single';
        }
        return false;
    };

    canAddBurdenLot = (lotCollection, ref, index) => {
        return !this.isSingleBurdenLot() && this.canAddLot(lotCollection, ref, index);
    };

    canAddLot = (lotCollection, ref, index) => {
        if (isDefined(lotCollection)) {
            if (lotCollection.length > 1) {
                // More than one lot
                return true;
            } else {
                return ref.lot; // single lot defined
            }
        } else {
            return false;
        }
    };

    addLot = lotCollection => {
        console.debug(this.title + ' controller addLot ');
        if (isDefined(lotCollection)) {
            lotCollection.push({lot: '', apportionment: ''});
        }
    };

    canRemoveLot = lotCollection => {
        if (isDefined(lotCollection)) {
            return lotCollection.length > 1;
        }

        return false;
    };

    removeLot = (lotCollection, reference) => {
        console.debug(this.title + ' controller removeLot');
        if (isDefined(lotCollection)) {
            lotCollection.splice(lotCollection.indexOf(reference), 1);
        }
    };

    invalidLotOrRangeError = () => {
        if (this.isSingleBurdenLot()) {
            return 'Enter valid single lot number.';
        }
        return 'Lot has to be a number N or a number range N-N.';
    };

    hasApportionment = () => {
        if (this.interestNotificationType && this.interestNotificationType.upkeepApportionment) {
            return this.interestNotificationType.upkeepApportionment === true;
        }
        return false;
    };

    apportionmentChanged = (form, name, element, benefitingDominantLot) => {
        console.debug(this.title + ' controller apportionment Changed');

        if (benefitingDominantLot.apportionment != null && (benefitingDominantLot.lot == undefined || benefitingDominantLot.lot.trim() == '')) {
            form.controls[name + 'lot' + element].dirty = true;
            form.controls[name + 'lot' + element].setErrors({
                invalidLotOrRange: true
            });
        } else {
            form.controls[name + 'lot' + element].setErrors(null);
        }
    };

    showBeneficiaries = () => {
        return this.beneficiaryOptions.length || this.showBenefitLotsThis() || this.showBenefitLotsOther();
    };

    showBenefitLotsThis = () => {
        return (
            this.beneficiarySelected &&
            this.interestNotificationType &&
            this.interestNotificationType.benefitLotsThis &&
            this.interestNotificationType.benefitLotsThis.includes(this.parentTenureType)
        );
    };

    showBenefitLotsOther = () => {
        return (
            this.beneficiarySelected &&
            this.interestNotificationType &&
            this.interestNotificationType.benefitLotsOther &&
            this.interestNotificationType.benefitLotsOther.includes(this.parentTenureType)
        );
    };

    getPlanNumberExcludePattern = () => {
        return `^${this.planModelService.getModel().planType.smpId + this.planModelService.getModel().planNumber}$`;
    };

    benefitLotsOtherRequired = () => {
        return !this.showBenefitLotsThis() || this.beneficiaryLots.every(bl => !bl.lot);
    };

    planLotChanged = (form, name, element, benefitingDominantLot, planRow) => {
        this.lotChanged(form, name, planRow.beneficiaryLots, element, benefitingDominantLot);
    };

    addNewLotsOnPlanRow = () => {
        this.beneficiaryLotsOtherPlan.push({planNumber: '', beneficiaryLots: [{lot: '', apportionment: ''}]});
    };

    removePlanAndLots = (lotCollection, reference) => {
        console.debug(this.title + ' controller removeLot');
        if (isDefined(lotCollection)) {
            lotCollection.splice(lotCollection.indexOf(reference), 1);
        }
    };

    showSameOwnerQuestion = () => {
        const planPurpose = this.planModelService.getModel().planPurpose;
        return planPurpose && this.interestNotificationType && this.interestNotificationType.cadSameOwnerPlanPurpose === planPurpose.id;
    };

    lgaChanged = (form, element) => {
        form.controls['lga'].setErrors({
            notSelected: true,
            required: true
        });
    };

    lgaSelected = (form, element, item) => {
        form.controls['lga'].setErrors(null);
        this.localGovernmentAuthoritySelected = item;
    };

    private markAllAsDirty() {
        Object.keys(this.newInterestNotificationModalForm.controls).forEach(controlName => {
            const control = this.newInterestNotificationModalForm.controls[controlName];
            control.markAsDirty();
        });
    }

    save = form => {
        console.debug(this.title + ' controller save');

        this.markAllAsDirty();

        let valid = true;
        if (form.burdensAffectedTenureForm) {
            form.burdensAffectedTenureForm.setSubmitted();
            valid = form.burdensAffectedTenureForm.valid;
        }

        if (!valid) {
            return;
        }

        if (!form.valid) {
            return;
        }

        this.checkInterestLinks()
            .pipe(
                mergeMap(doSave => {
                    console.debug(this.title + `doSave = ${doSave}`);
                    if (doSave) {
                        return this.spatialUtilService.save().pipe(
                            mergeMap(() => {
                                const newInterest = this.convertFromUIToDomain();
                                console.debug(`Closing modal with new interest ${newInterest}`);
                                this.modalClosed.emit(newInterest);
                                return of(null);
                            })
                        );
                    } else {
                        this.modalClosed.emit('no');
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: () => console.debug('done'),
                error: err => console.error(err)
            });
    };

    cancel = form => {
        console.debug(this.title + ' controller cancel');
        if (form.pristine) {
            this.modalClosed.emit('no');
            return;
        }
        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: `You are about to lose all changes. Are you sure you wish to continue?`
            })
            .subscribe(action => {
                console.debug(action);
                if (action === 'yes') {
                    this.modalClosed.emit('no');
                }
            });
    };

    checkInterestLinks = () => {
        console.debug(`${this.title} controller checkInterestLinks`);

        if (!this.interest) {
            console.debug(`${this.title} controller checkInterestLinks - NEW interest`);
            return of(true);
        }

        const newInterest = this.convertFromUIToDomain();
        if (
            newInterest.id === this.interest.id &&
            equals(
                (newInterest.interestCode.scope || []).map(s => s.toLowerCase()).sort(),
                (this.interest.interestCode.scope || []).map(s => s.toLowerCase()).sort()
            )
        ) {
            console.debug(`${this.title} controller checkInterestLinks - UNCHANGED interest code scope`);
            return of(true);
        }

        console.debug(`${this.title} controller checkInterestLinks - CHANGED interest code scope`);

        // all interests cleared - the interestReconciliationService will apply updates on close
        this.spatialUtilService.clearInterests();
        return of(true);
    };

    convertFromUIToDomain = () => {
        const domainModel: any = {};
        domainModel.id = this.id;
        domainModel.createdBy = this.createdByOptionSelected;
        domainModel.interestCode = this.interestNotificationType;
        domainModel.originDocument = this.originDocumentNumber;
        domainModel.subject = this.subject;
        domainModel.userEnteredComments = this.comments;

        const burdenedLotList = [];
        if (this.burdenedLots && this.burdenedLots.length > 0) {
            this.burdenedLots.forEach(b => {
                if (b.lot) {
                    burdenedLotList.push({
                        id: b.id,
                        startLot: this.getStartLot(b.lot),
                        endLot: this.getEndLot(b.lot),
                        upkeepApportionment: b.apportionment
                    });
                }
            });
        }
        domainModel.burdenedLots = this.consolidateLots(burdenedLotList);

        domainModel.benefits = [];

        if (this.beneficiarySelected) {
            // Lot beneficiaries
            if (this.beneficiaryLots && this.beneficiaryLots.length > 0 && this.beneficiaryLots[0].lot) {
                // If anything defined
                const lotOnThisPlan = {category: 'LOTS_ON_PLAN', lots: []};
                domainModel.benefits.push(lotOnThisPlan);
                this.beneficiaryLots.forEach(b => {
                    if (b.lot) {
                        lotOnThisPlan.lots.push({
                            id: b.id,
                            startLot: this.getStartLot(b.lot),
                            endLot: this.getEndLot(b.lot),
                            upkeepApportionment: b.apportionment
                        });
                    }
                });
            }
            if (this.beneficiaryLotsOtherPlan && this.beneficiaryLotsOtherPlan.length) {
                const beneficiaryLotsOtherPlanMap = this.beneficiaryLotsOtherPlan.reduce((acc, v) => {
                    if (v.planNumber) {
                        const filteredBeneficiaryLots = v.beneficiaryLots.filter(bl => bl.lot);
                        if (filteredBeneficiaryLots.length) {
                            if (!acc[v.planNumber]) {
                                acc[v.planNumber] = [];
                            }
                            acc[v.planNumber].push(...filteredBeneficiaryLots);
                            acc[v.planNumber] = acc[v.planNumber].filter(
                                (bl, i, a) => a.findIndex(bl2 => bl2.lot === bl.lot && bl2.upkeepApportionment === bl.upkeepApportionment) === i
                            );
                        }
                    }
                    return acc;
                }, {});

                Object.entries(beneficiaryLotsOtherPlanMap).forEach(kv => {
                    const planNumber = kv[0];
                    const beneficiaryLots: any = kv[1];
                    domainModel.benefits.push({
                        planReference: planNumber,
                        category: 'LOTS_ON_OTHER_PLAN',
                        lots: beneficiaryLots.map(bl => ({
                            id: bl.id,
                            startLot: this.getStartLot(bl.lot),
                            endLot: this.getEndLot(bl.lot),
                            upkeepApportionment: bl.apportionment
                        }))
                    });
                });
            }
        } else {
            // Named beneficiaries
            if (this.namedBeneficiarySelected === 'lga') {
                domainModel.benefits = [{category: 'LOCAL_GOVERNMENT_AUTHORITY', lots: []}];
                domainModel.benefits[0].lga = this.localGovernmentAuthoritySelected;
            }
            if (this.namedBeneficiarySelected === 'freetext') {
                domainModel.benefits = [{category: 'FREE_TEXT_BENEFICIARY', lots: []}];
                domainModel.benefits[0].freeText = this.beneficiaryDescription;
            }
            if (this.namedBeneficiarySelected === 'benefittype') {
                domainModel.benefits = [{category: 'SELECTED_BENEFICIARY', lots: []}];
                domainModel.benefits[0].type = this.selectedBeneficiary;
            }
            if (this.namedBeneficiarySelected === 'seedoc') {
                domainModel.benefits = [{category: 'SEE_DOCUMENT', lots: []}];
            }
            if (this.additionalMandatoryBeneficiary) {
                domainModel.benefits.push({category: 'ADDITIONAL_BENEFICIARY', lots: [], freeText: this.additionalMandatoryBeneficiary});
            }
            if (domainModel.burdenedLots && domainModel.burdenedLots.length > 0) {
                domainModel.burdenedLots = this.consolidateLots(domainModel.burdenedLots);
            }
        }

        domainModel.benefits.forEach(benefit => {
            if (benefit.lots && benefit.lots.length > 0) {
                benefit.lots = this.consolidateLots(benefit.lots);
            }
        });

        domainModel.burdenedAffectedTenures = this.burdenedAffectedTenures.slice();

        domainModel.sameOwner = this.sameOwner;

        return domainModel;
    };

    convertFromDomainToUI = domainModel => {
        this.id = domainModel.id; // save ID
        this.createdByOptionSelected = domainModel.createdBy;
        this.interestNotificationType = domainModel.interestCode;
        this.interestNotificationTypeName = this.interestNotificationType.name;
        this.originDocumentNumber = domainModel.originDocument;
        this.subject = domainModel.subject;
        this.comments = domainModel.userEnteredComments;
        this.statutoryReference = domainModel.interestCode.statutoryReference;
        this.purpose = domainModel.interestCode.purpose;

        if (domainModel.burdenedLots && domainModel.burdenedLots.length > 0) {
            this.burdenedLots = [];
            domainModel.burdenedLots.forEach(lot => {
                this.burdenedLots.push({id: lot.id, lot: this.getLotsValue(lot), apportionment: lot.upkeepApportionment});
            });
        } else {
            this.burdenedLots = [{lot: '', apportionment: ''}];
        }

        this.beneficiaryLots = [];
        this.beneficiaryLotsOtherPlan = [];
        if (domainModel.benefits && domainModel.benefits.length > 0) {
            domainModel.benefits.forEach(benefit => {
                if (benefit.category === 'LOTS_ON_PLAN') {
                    benefit.lots.forEach(lot => {
                        this.beneficiaryLots.push({id: lot.id, lot: this.getLotsValue(lot), apportionment: lot.upkeepApportionment});
                    });
                    this.beneficiarySelected = true;
                } else if (benefit.category === 'LOTS_ON_OTHER_PLAN') {
                    let otherPlan = {planNumber: benefit.planReference, beneficiaryLots: []};
                    benefit.lots.forEach(lot => {
                        otherPlan.beneficiaryLots.push({id: lot.id, lot: this.getLotsValue(lot), apportionment: lot.upkeepApportionment});
                    });
                    this.beneficiaryLotsOtherPlan.push(otherPlan);
                    this.beneficiarySelected = true;
                } else {
                    this.beneficiarySelected = false;
                    if (benefit.category === 'SEE_DOCUMENT') {
                        this.namedBeneficiarySelected = 'seedoc';
                    } else if (benefit.category === 'LOCAL_GOVERNMENT_AUTHORITY') {
                        this.namedBeneficiarySelected = 'lga';
                        this.localGovernmentAuthoritySelected = benefit.lga;
                    } else if (benefit.category === 'SELECTED_BENEFICIARY') {
                        this.namedBeneficiarySelected = 'benefittype';
                        this.selectedBeneficiary = benefit.type;
                    } else if (benefit.category === 'FREE_TEXT_BENEFICIARY') {
                        this.namedBeneficiarySelected = 'freetext';
                        this.beneficiaryDescription = benefit.freeText;
                    } else if (benefit.category === 'ADDITIONAL_BENEFICIARY') {
                        this.additionalMandatoryBeneficiary = benefit.freeText;
                    }
                }
            });
        }
        if (this.beneficiaryLots.length === 0) {
            this.beneficiaryLots = [{lot: '', apportionment: ''}];
        }
        if (this.beneficiaryLotsOtherPlan.length === 0) {
            this.beneficiaryLotsOtherPlan = [{planNumber: '', beneficiaryLots: [{lot: '', apportionment: ''}]}];
        }

        this.burdenedAffectedTenures = domainModel.burdenedAffectedTenures ? domainModel.burdenedAffectedTenures.slice() : [];

        this.sameOwner = domainModel.sameOwner;
    };

    getLotsValue = lot => {
        let lots = '';
        if (lot.startLot) {
            lots = lots + lot.startLot;
            if (lot.endLot) {
                lots = lots + '-' + lot.endLot;
            }
        }
        return lots;
    };

    consolidateLots = lots => {
        const map = this.breakApportionments(lots);
        const keys = Object.keys(map);
        const resultLots = [];
        keys.forEach(key => {
            const ranges = this.createLotRanges(map[key]);
            ranges.forEach(range => {
                resultLots.push(range);
            });
        });
        return resultLots;
    };

    breakApportionments = lots => {
        const map: any = {};
        lots.forEach(lot => {
            if (!lot.upkeepApportionment) {
                lot.upkeepApportionment = '';
            }
            if (map[lot.upkeepApportionment]) {
                map[lot.upkeepApportionment].push(lot);
            } else {
                map[lot.upkeepApportionment] = [];
                map[lot.upkeepApportionment].push(lot);
            }
        });
        return map;
    };

    createLotRanges = lots => {
        const map = {};
        const upkeepApportionment = lots[0].upkeepApportionment;
        lots.forEach(lot => {
            if (lot.endLot) {
                const start = parseInt(lot.startLot);
                const end = parseInt(lot.endLot);
                for (let i = start; i < end + 1; i++) {
                    map[i] = i;
                }
            } else {
                const start = parseInt(lot.startLot);
                map[start] = parseInt(lot.startLot);
            }
        });
        const keys = Object.keys(map).sort(function compare(a: any | number | bigint, b: any | number | bigint) {
            return a - b;
        });
        const lotRanges = [];
        let previousKey = -1;
        const lotBlocks = [];
        let currentBlock = [];
        for (let i = 0; i < keys.length; i++) {
            const key = Number(keys[i]);

            if (previousKey == -1) {
                // First key always goes first
                currentBlock.push(key);
            } else {
                if (key === previousKey + 1) {
                    currentBlock.push(key);
                } else {
                    lotBlocks.push(currentBlock); // Keep an old block
                    currentBlock = [];
                    currentBlock.push(key);
                }
            }
            previousKey = key;
        }
        // Keep latest block
        lotBlocks.push(currentBlock);

        // Create list of affected blocks
        for (let i = 0; i < lotBlocks.length; i++) {
            if (lotBlocks[i].length == 1) {
                lotRanges.push({startLot: lotBlocks[i][0], upkeepApportionment: upkeepApportionment});
            } else {
                if (lotBlocks[i].length > 1) {
                    // first and last element value
                    lotRanges.push({
                        startLot: lotBlocks[i][0],
                        endLot: lotBlocks[i][lotBlocks[i].length - 1],
                        upkeepApportionment: upkeepApportionment
                    });
                } else {
                    // Zero length (unfilled lots)
                }
            }
        }
        return lotRanges;
    };
}
